import { memo, useEffect, useRef, useState } from "react";

import { useApolloClient } from "@apollo/client";
import { css } from "@emotion/react";
import { DateTimePicker } from "@hl/base-components/lib/DateTimePicker";
import ModalSection from "@hl/base-components/lib/modal/ModalSection";
import { SECONDARY_COLOR } from "@hl/base-components/lib/theme/button";
import ErrorLinkContainer from "@hl/communities-app/lib/components/ErrorLinkContainer";
import { topMargin } from "@hl/communities-app/lib/styles/global/layout";
import useAnalytics from "@hl/shared-features/lib/features/analytics/useAnalytics";
import { useAuth } from "@hl/shared-features/lib/features/auth/AuthContext";
import {
  TransactionStateType,
  useTransactionState,
  useTransactionsDispatch,
} from "@hl/shared-features/lib/features/evm-tx/TransactionContext";
import CryptoCurrencyInput from "@hl/shared-features/lib/features/input/CryptoCurrencyInput";
import { logError } from "@hl/shared-features/lib/services/logger";
import {
  ActionIcon,
  Button,
  Card,
  Divider,
  Grid,
  Group,
  NumberInput,
  NumberInputHandlers,
  Space,
  Stack,
  Switch,
  Text,
  TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { ethers } from "ethers";
import {
  PauseCircleFill,
  PlayCircleFill,
} from "~/../../base-components/lib/assets/icons.generated";
import {
  STATUS_COLOR,
  TEXT_COLOR,
} from "~/../../base-components/lib/theme/colors";

import {
  OnChainMintVectorUpdateInput,
  PriceDropTimePeriod,
  PriceType,
  TransactionType,
  _CollectionType,
} from "apollo/graphql.generated";
import GateSelect from "components/GateSelect";
import { ALCHEMY_API_KEY_ENS } from "config";
import { getPriceDropIntervalTime } from "utils/duthAuctions";
import DutchAuctionsCurrencyInput from "~/currency/DutchAuctionsCurrencyInput";
import DutchAuctionsPriceDropIntervalInput from "~/currency/DutchAuctionsPriceDropIntervalInput";
import { useGatesQuery } from "~/features/gates/queries/gates.graphql.generated";

import {
  GetManageTokenCollectionDocument,
  GetManageTokenMintVectorsDocument,
  useGetUpdateMintVectorTxArgsLazyQuery,
  useUpdateManageTokenMintVectorMutation,
} from "../../queries.graphql.generated";

export interface EditSaleModalProps {
  mintVectorId: string;
  startDate: Date;
  endDate?: Date;
  price: string;
  chainId: string;
  maxPerUser: number;
  nonTransferable: boolean;
  paymentRecipient: string;
  paymentRecipientEOA: string;
  gateId: string;
  paused: boolean;
  onClose: () => void;
  priceType?: string | null;
  scheduledPrices?: string[] | null;
  priceDropInterval?: PriceDropTimePeriod[] | null;
  collectionId: string;
  collectionType: _CollectionType;
  isDirectMint: boolean;
}

export interface EditSaleFormValues {
  startDate: Date;
  endDate?: Date;
  price: string;
  shouldLimitNumberOfMintPerWallet: boolean;
  numberOfMintPerWalletLimit: number;
  nonTransferable: boolean;
  paymentRecipient: string;
  paymentRecipientEOA: string;
  gateId: string;
  paused: boolean;
  scheduledPrices?: string[] | null;
  priceDropInterval?: PriceDropTimePeriod[] | null;
}

export const useUpdateMintVectorOnChain = ({
  mintVectorId,
  collectionId,
  collectionType,
  onFinish,
}: {
  mintVectorId: string;
  collectionId: string;
  collectionType: _CollectionType;
  onFinish: () => void;
}) => {
  const [error, setError] = useState<string>("");
  const getNewTxnId = () => `txn-${Date.now()}`;
  const [txnId, setTxnId] = useState<string>(getNewTxnId());
  const transactionDispatch = useTransactionsDispatch();
  const transactionState = useTransactionState(txnId);
  const [generateTxArgs] = useGetUpdateMintVectorTxArgsLazyQuery();
  const { walletAddress } = useAuth();

  const startUpdate = async (input: OnChainMintVectorUpdateInput) => {
    setError("");
    try {
      if (!walletAddress) {
        return;
      }
      const args = await generateTxArgs({
        variables: {
          mintVectorId,
          data: input,
        },
      });
      const txnId = getNewTxnId();

      transactionDispatch?.({
        type: "START_TRANSACTION",
        payload: {
          id: txnId,
          entityId: mintVectorId,
          transactionType: TransactionType.EVM_UPDATE_MINT_VECTOR,
          collectionType,
          args: args.data?.mintVectorUpdateTxArgs,
          metadata: {
            collectionId,
            nftContractAddress: walletAddress,
            txType: TransactionType.EVM_UPDATE_MINT_VECTOR,
          },
        },
      });
      setTxnId(txnId);
    } catch (e) {
      setError("Failed to save changes.");
      logError({
        e,
        message: "[UpdateMintVectorOnChain] Failed to update",
      });
    }
  };
  let buttonLabel = null;
  let buttonDisabled = false;
  if (transactionState && !transactionState.error && !!transactionState.args) {
    if (transactionState.type === TransactionStateType.WaitingSignedTx) {
      buttonLabel = "Waiting for user signature...";
      buttonDisabled = true;
    } else if (transactionState.type !== TransactionStateType.Done) {
      buttonLabel = "Updating ...";
      buttonDisabled = true;
    }
  }

  const apolloClient = useApolloClient();
  useEffect(() => {
    if (
      transactionState?.type === TransactionStateType.Done &&
      // having receipt ensures that `updateTx` is called already, that would trigger sync
      transactionState.receipt
    ) {
      const timeout = setTimeout(() => {
        apolloClient.refetchQueries({
          include: [
            GetManageTokenCollectionDocument,
            GetManageTokenMintVectorsDocument,
          ],
        });
        onFinish();
      }, 1000);

      return () => clearTimeout(timeout);
    }
  }, [transactionState]);

  return {
    error,
    startUpdate,
    buttonLabel,
    buttonDisabled,
    transactionState,
  };
};

const EditSaleModal = ({
  collectionId,
  collectionType,
  isDirectMint,
  ...data
}: EditSaleModalProps) => {
  const { trackEvent, EventType } = useAnalytics();
  const [loadingForm, setLoadingForm] = useState<boolean>(false);

  const [error, setError] = useState<string>("");
  const limitNumberOfMintsHandlers = useRef<NumberInputHandlers>();
  const { data: gatesData, loading: gatesLoading } = useGatesQuery();
  const form = useForm<EditSaleFormValues>({
    initialValues: {
      startDate: data.startDate,
      endDate: data.endDate,
      price: data.price,
      shouldLimitNumberOfMintPerWallet: data.maxPerUser !== 0,
      numberOfMintPerWalletLimit: data.maxPerUser,
      nonTransferable: data.nonTransferable,
      paymentRecipient: data.paymentRecipient,
      paymentRecipientEOA: data.paymentRecipient,
      gateId: data.gateId,
      paused: data.paused,
      scheduledPrices: data.scheduledPrices,
      priceDropInterval:
        data.priceDropInterval?.map((period) => ({
          unit: period.unit,
          value: period.value,
        })) || null,
    },
    validate: (values) => ({
      price:
        (!data.priceType || data.priceType === PriceType.Fixed) &&
        values.price === null
          ? "Price is required"
          : null,
      startDate: !values.startDate
        ? "Sale start date is required"
        : values.endDate && values.endDate < values.startDate
        ? "Sale start date must be less than or equal to sale end date"
        : null,
      endDate:
        values.endDate && values.endDate < values.startDate
          ? "Sale end date must be greater than or equal to sale start date"
          : null,
      scheduledPrices:
        data.priceType === PriceType.DutchAuction &&
        !values.scheduledPrices?.length
          ? "Price is required"
          : data.priceType === PriceType.DutchAuction &&
            values.scheduledPrices!.length < 2
          ? "Price needs to be more than 1"
          : null,
      priceDropInterval:
        data.priceType === PriceType.DutchAuction &&
        !values.priceDropInterval?.length
          ? "Drop interval is required"
          : data.priceType === PriceType.DutchAuction &&
            values.priceDropInterval?.every((period) => period.value === 0)
          ? "Drop interval must be greater 0"
          : data.priceType === PriceType.DutchAuction &&
            values.endDate &&
            values.scheduledPrices?.length &&
            values.endDate.getTime() -
              (values.startDate.getTime() +
                getPriceDropIntervalTime(values.priceDropInterval) *
                  (values.scheduledPrices.length - 1)) <
              0
          ? "Drop interval cannot exceed the end time"
          : null,
    }),
  });

  const [updateMintVector, { loading, error: updateMutationError, called }] =
    useUpdateManageTokenMintVectorMutation({
      refetchQueries: [
        GetManageTokenCollectionDocument,
        GetManageTokenMintVectorsDocument,
      ],
    });

  const {
    startUpdate: startOnchainUpdate,
    buttonLabel,
    buttonDisabled,
  } = useUpdateMintVectorOnChain({
    mintVectorId: data.mintVectorId,
    collectionType,
    collectionId,
    onFinish: data.onClose,
  });

  useEffect(() => {
    form.validate();
  }, [form.values]);

  useEffect(() => {
    if (!loading && !updateMutationError && called) {
      data.onClose();
    }
  }, [loading, updateMutationError, called]);

  useEffect(() => {
    setLoadingForm(gatesLoading);
  }, [gatesLoading]);

  const ensUrl = `https://eth-mainnet.alchemyapi.io/v2/${ALCHEMY_API_KEY_ENS}`;
  const provider = ALCHEMY_API_KEY_ENS
    ? new ethers.providers.JsonRpcProvider(ensUrl)
    : ethers.getDefaultProvider();

  const onSetPaymentRecipientEOA = async (val: string) => {
    form.setFieldValue("paymentRecipient", val);
    if (val.endsWith(".eth")) {
      setLoadingForm(true);
      const address = await provider.resolveName(val);
      if (!address) {
        form.setFieldError("paymentRecipientEOA", "Invalid ENS");
      } else {
        form.setFieldValue("paymentRecipient", address);
      }
      setLoadingForm(false);
    }
  };

  const isFixedPrice = !data.priceType || data.priceType === PriceType.Fixed;
  return (
    <>
      <form
        onSubmit={form.onSubmit(async (values) => {
          setError("");
          try {
            const onChainSubmission = {
              start: values.startDate.toString(),
              // null is used to remove a date, sending undefined will not change it.
              end: values.endDate?.toString() || null,
              price: values.price.toString(),
              maxPerUser: form.values.shouldLimitNumberOfMintPerWallet
                ? form.values.numberOfMintPerWalletLimit
                : 0,
              paymentRecipient: form.values.paymentRecipient,
            };
            const submission = {
              ...onChainSubmission,
              mintVectorId: data.mintVectorId,
              priceDropInterval: values.priceDropInterval,
              scheduledPrices: values.scheduledPrices,
              gateId: values.gateId ?? "",
              paused: values.paused,
            };
            if (
              !values.paymentRecipient ||
              !ethers.utils.isAddress(values.paymentRecipient)
            ) {
              form.setFieldError(
                "paymentRecipientEOA",
                "Valid Payment recipient is required"
              );
              return;
            }

            if (isDirectMint) {
              await startOnchainUpdate(onChainSubmission);
            } else {
              await updateMintVector({ variables: { data: submission } });
            }
            trackEvent(EventType.EditToken_EditSaleSaved, submission);
          } catch (e) {
            setError("Failed to save changes.");
            logError({
              e,
              message: "[EditSaleModal] Failed to update mint vector",
            });
          }
        })}
      >
        {data.paused && (
          <Card mb="md" p="md">
            <Grid>
              <Grid.Col span={10}>
                <Group>
                  {form.values.paused ? (
                    <>
                      <PauseCircleFill width={20} height={20} />
                      <Text
                        weight={500}
                        size="lg"
                        style={{ wordWrap: "break-word" }}
                        color={TEXT_COLOR.SECONDARY}
                      >
                        Mint paused
                      </Text>
                    </>
                  ) : (
                    <>
                      <PlayCircleFill width={20} height={20} />
                      <Text
                        weight={500}
                        size="lg"
                        style={{ wordWrap: "break-word" }}
                        color={STATUS_COLOR.AFFIRMATIVE}
                      >
                        Mint active
                      </Text>
                    </>
                  )}
                </Group>
                <Group mt={6}>
                  <Text size="sm">
                    The metadata for this collection is being updated. The mint
                    will automatically restart as soon as the update is
                    complete. You can manually restart the mint if the update
                    failed.
                  </Text>
                </Group>
              </Grid.Col>
              <Grid.Col span={2} sx={{ alignSelf: "center" }}>
                <Button
                  color={SECONDARY_COLOR}
                  onClick={() => {
                    form.setValues({ paused: !form.values.paused });
                  }}
                >
                  {form.values.paused ? "Restart" : "Pause"}
                </Button>
              </Grid.Col>
            </Grid>
          </Card>
        )}
        {isFixedPrice ? (
          <CryptoCurrencyInput
            label="Price"
            placeholder="Token price"
            chainId={data.chainId}
            styles={(theme) => ({
              description: {
                fontSize: theme.fontSizes.sm,
                color: theme.colors.dark[9],
              },
            })}
            {...form.getInputProps("price")}
          />
        ) : (
          <Stack spacing={24}>
            <DutchAuctionsCurrencyInput
              value={form.values.scheduledPrices}
              chainId={data.chainId}
              error={form.getInputProps("scheduledPrices").error}
              onChange={form.getInputProps("scheduledPrices").onChange}
            />
            <DutchAuctionsPriceDropIntervalInput
              value={form.values.priceDropInterval}
              error={form.getInputProps("priceDropInterval").error}
              onChange={form.getInputProps("priceDropInterval").onChange}
            />
          </Stack>
        )}

        <Space h="xl" />
        <DateTimePicker
          dropdownType="modal"
          label={
            <>
              <Group spacing={4}>Mint start date</Group>
            </>
          }
          clearable={false}
          {...form.getInputProps("startDate")}
          minDate={new Date()}
        />
        <Space h="lg" />
        <DateTimePicker
          dropdownType="modal"
          label={
            <>
              <Group spacing={4}>Mint end date</Group>
            </>
          }
          placeholder="None"
          {...form.getInputProps("endDate")}
        />
        <Space h="lg" />
        {!isDirectMint && (
          <>
            <GateSelect
              description="Select a gate to determine who can mint this collection"
              gates={gatesData?.gates ?? []}
              value={form.values.gateId ?? ""}
              onChange={(newGateId: string) =>
                form.setValues({ gateId: newGateId })
              }
              isGatingMint
              isGatingAuction={false}
            />
            <Space h="lg" />
          </>
        )}

        <Text pb="xs" size="sm" component="label">
          Limit mints
        </Text>
        <Card p="xs">
          <Group position="apart" align="center">
            <Text size="sm">Limit number of mints per wallet</Text>
            <Switch
              sx={{
                input: {
                  height: "100%",
                },
              }}
              {...form.getInputProps("shouldLimitNumberOfMintPerWallet")}
              checked={
                form.getInputProps("shouldLimitNumberOfMintPerWallet").value
              }
            />
          </Group>
          {form.values.shouldLimitNumberOfMintPerWallet && (
            <>
              <Divider my="xs" />
              <Group position="apart" align="stretch">
                <Text size="sm">Maximum mints allowed per wallet</Text>
                <Group spacing={0}>
                  <NumberInput
                    min={1}
                    max={10000}
                    {...form.getInputProps("numberOfMintPerWalletLimit")}
                    handlersRef={limitNumberOfMintsHandlers}
                    hideControls
                    hidden
                  />
                  <Text size="sm" mr="8px">
                    {form.values.numberOfMintPerWalletLimit}
                  </Text>
                  <ActionIcon
                    size={20}
                    color={SECONDARY_COLOR}
                    onClick={() =>
                      limitNumberOfMintsHandlers?.current?.decrement()
                    }
                    mr="1px"
                  >
                    –
                  </ActionIcon>
                  <ActionIcon
                    size={20}
                    color={SECONDARY_COLOR}
                    onClick={() =>
                      limitNumberOfMintsHandlers?.current?.increment()
                    }
                  >
                    +
                  </ActionIcon>
                </Group>
              </Group>
            </>
          )}
        </Card>
        <Space h="lg" />
        <TextInput
          placeholder="e.g. vitalik.eth, 0x0000000000000000000000000000000000000000"
          onInput={(e) => onSetPaymentRecipientEOA(e.currentTarget.value)}
          spellCheck={false}
          label="Primary sales payout address"
          {...form.getInputProps("paymentRecipientEOA")}
        />
        <ModalSection>
          <Button
            type="submit"
            size="xl"
            fullWidth
            loading={loading}
            disabled={loadingForm || buttonDisabled}
          >
            {buttonLabel ?? "Save updates"}
          </Button>
          {isDirectMint && (
            <Text size="xs" align="center" mt={8}>
              This action requires a transaction
            </Text>
          )}
          {error && (
            <div
              css={css`
                ${topMargin("3/6px")};
              `}
            >
              <ErrorLinkContainer onClose={() => setError("")}>
                <Text>{error}</Text>
              </ErrorLinkContainer>
            </div>
          )}
        </ModalSection>
      </form>
    </>
  );
};

export default memo(EditSaleModal);
